import { Drawer, Icon } from '@kollez/design-system';
import type { CreatePartnerBody } from '@/lib/queries';
import {
  PARTNER_TYPE_OPTIONS,
  INDUSTRY_OPTIONS,
  COMPANY_SIZE_OPTIONS,
  isHiringPartnerType,
} from '@/config/partnerOptions';

/**
 * Shared "New partner" drawer used from the group Partners page and the TPO Companies page.
 *
 * Every Partner is created together with its admin User + invite in a single transaction.
 * The admin block is REQUIRED. A collegePicker is required for group / university admins
 * (leaf-tenant guard). Non-hiring partner types render a warning notice about the limited
 * portal experience during pilot.
 */
interface Props {
  open: boolean;
  onClose: () => void;
  form: CreatePartnerBody;
  setForm: React.Dispatch<React.SetStateAction<CreatePartnerBody>>;
  onSubmit: () => void;
  submitting?: boolean;
  errorText?: string | null;
  /**
   * When provided, renders a required college picker (group / university admin case).
   * Leaf-level callers (TPO, principal) omit this — partner lands at their own tenant.
   */
  collegePicker?: {
    label?: string;
    options: Array<{ _id: string; name: string }>;
    loading?: boolean;
  };
}

const isValidUrl = (u: string): boolean => {
  if (!u) return true;
  try {
    new URL(u);
    return true;
  } catch {
    return false;
  }
};

const isValidEmail = (e: string): boolean => /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(e);

export function NewPartnerDrawer({ open, onClose, form, setForm, onSubmit, submitting, errorText, collegePicker }: Props) {
  const adminReady =
    form.admin.fullName.trim().length >= 2 &&
    isValidEmail(form.admin.email.trim());
  const canSubmit =
    form.name.trim().length >= 2 &&
    !!form.type &&
    isValidUrl(form.website ?? '') &&
    (collegePicker ? !!form.collegeId : true) &&
    adminReady;
  const isNonHiring = !isHiringPartnerType(form.type);

  return (
    <Drawer open={open} onClose={onClose} title="New partner" size="md">
      <div className="grid gap-4">
        <p className="helper m-0">
          Onboard a hiring partner into your tenant. The partner admin below gets an invite link to
          activate their account and start managing this partner on Kollez.
        </p>

        <label className="grid gap-1.5">
          <span className="label">Company name *</span>
          <input
            className="input"
            value={form.name}
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            placeholder="e.g. Wipro Limited"
          />
        </label>

        {collegePicker && (
          <label className="grid gap-1.5">
            <span className="label">{collegePicker.label ?? 'College this partner hires from'} *</span>
            <select
              className="select"
              value={form.collegeId ?? ''}
              onChange={(e) => setForm((f) => ({ ...f, collegeId: e.target.value || undefined }))}
              disabled={collegePicker.loading}
            >
              <option value="">{collegePicker.loading ? 'Loading colleges…' : 'Pick a college…'}</option>
              {collegePicker.options.map((c) => (
                <option key={c._id} value={c._id}>{c.name}</option>
              ))}
            </select>
            <span className="helper">Partner data lands at the college's tenant, not the group tenant, so students in that college can see their drives.</span>
          </label>
        )}

        <label className="grid gap-1.5">
          <span className="label">Partner type *</span>
          <select
            className="select"
            value={form.type}
            onChange={(e) => setForm((f) => ({ ...f, type: e.target.value }))}
          >
            {PARTNER_TYPE_OPTIONS.map(([v, l]) => (
              <option key={v} value={v}>{l}</option>
            ))}
          </select>
          {isNonHiring && (
            <p
              className="helper"
              data-testid="partner-type-limited-notice"
              style={{ color: 'rgb(var(--ink-warning))' }}
            >
              This partner type currently has a limited portal experience while we finalize the
              certification / internship tracking module. You can still onboard them, sign MoUs, and
              manage records manually via the TPO tools.
            </p>
          )}
        </label>

        <div className="grid gap-4 md:grid-cols-2">
          <label className="grid gap-1.5">
            <span className="label">Industry</span>
            <select
              className="select"
              value={form.industry ?? ''}
              onChange={(e) => setForm((f) => ({ ...f, industry: e.target.value }))}
            >
              {INDUSTRY_OPTIONS.map(([v, l]) => (
                <option key={v} value={v}>{l}</option>
              ))}
            </select>
          </label>

          <label className="grid gap-1.5">
            <span className="label">Company size</span>
            <select
              className="select"
              value={form.companySize ?? ''}
              onChange={(e) => setForm((f) => ({ ...f, companySize: e.target.value }))}
            >
              {COMPANY_SIZE_OPTIONS.map(([v, l]) => (
                <option key={v} value={v}>{l}</option>
              ))}
            </select>
          </label>
        </div>

        <label className="grid gap-1.5">
          <span className="label">Website</span>
          <input
            className="input"
            type="url"
            value={form.website ?? ''}
            onChange={(e) => setForm((f) => ({ ...f, website: e.target.value }))}
            placeholder="https://…"
          />
        </label>

        <label className="grid gap-1.5">
          <span className="label">Description</span>
          <textarea
            className="input"
            rows={3}
            value={form.description ?? ''}
            onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
            placeholder="Optional — one or two lines about what they do."
          />
        </label>

        <div className="card p-4 grid gap-3">
          <div>
            <h3 className="text-sm font-semibold m-0">Partner admin *</h3>
            <p className="helper m-0 mt-1">
              This person will manage this partner on Kollez. They'll get an invite link to
              activate their account. You can add more users after they've activated.
            </p>
          </div>
          <label className="grid gap-1.5">
            <span className="label">Full name *</span>
            <input
              className="input"
              value={form.admin.fullName}
              onChange={(e) => setForm((f) => ({ ...f, admin: { ...f.admin, fullName: e.target.value } }))}
              placeholder="e.g. Priya Ramesh"
            />
          </label>
          <label className="grid gap-1.5">
            <span className="label">Email *</span>
            <input
              className="input"
              type="email"
              value={form.admin.email}
              onChange={(e) => setForm((f) => ({ ...f, admin: { ...f.admin, email: e.target.value } }))}
              placeholder="e.g. priya@wipro.com"
            />
          </label>
          <label className="grid gap-1.5">
            <span className="label">Phone <span className="helper">(recommended)</span></span>
            <input
              className="input"
              value={form.admin.phone ?? ''}
              onChange={(e) => setForm((f) => ({ ...f, admin: { ...f.admin, phone: e.target.value } }))}
              placeholder="10-digit mobile"
            />
          </label>
        </div>

        {errorText && (
          <p className="helper m-0" style={{ color: 'rgb(var(--ink-danger))' }}>
            {errorText}
          </p>
        )}
      </div>

      <div className="drawer-foot">
        <button className="btn btn-outline" onClick={onClose} disabled={submitting}>
          Cancel
        </button>
        <button
          className="btn btn-primary ml-auto"
          disabled={!canSubmit || submitting}
          onClick={onSubmit}
        >
          <Icon name="plus" size={14} />
          {submitting ? 'Creating…' : 'Create partner'}
        </button>
      </div>
    </Drawer>
  );
}
